/**
 * BL-product-customizer.js
 * -----------------------------------------------------------------------------
 * Handles the logic for:
 * 1. Furniture Base configuration (Redirect-based)
 * 2. Add-on 3-way toggle (No Basin / Choose Basin / Vanity Top Only)
 * 3. Basin cascading filters (Color -> Thickness -> Design (Card) -> Config)
 * 4. AJAX Add to Cart (Unified for Product + Addon)
 *
 * Architecture:
 * - NO MutationObservers (they cause infinite loops with price updates)
 * - NO simulated .click() auto-selection (causes cascading infinite renders)
 * - Silent state propagation: set state directly, then render next step
 * - Price updates only happen at terminal state or on explicit user interaction
 */

if (!customElements.get('bl-product-customizer')) {
  function getCartAddonInterceptorRegistry() {
    if (!window.__blCartAddonInterceptor) {
      window.__blCartAddonInterceptor = {
        installed: false,
        originalFetch: window.fetch.bind(window),
        providers: new Map()
      };
    }
    return window.__blCartAddonInterceptor;
  }

  function installCartAddonFetchInterceptor() {
    const registry = getCartAddonInterceptorRegistry();
    if (registry.installed) return registry;

    registry.installed = true;

    window.fetch = function(url, config) {
      const requestUrl = typeof url === 'string' ? url : url?.url || '';

      if (!requestUrl.includes('/cart/add') || !config) {
        return registry.originalFetch.call(this, url, config);
      }

      const providers = Array.from(registry.providers.values()).sort((a, b) => (b.priority || 0) - (a.priority || 0));

      for (const provider of providers) {
        if (!provider || typeof provider.getBundlePayload !== 'function') continue;

        const payload = provider.getBundlePayload(config);
        if (!payload || !Array.isArray(payload.items) || !payload.items.length) continue;

        const newConfig = Object.assign({}, config);
        newConfig.headers = Object.assign({}, config.headers || {}, {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        });
        newConfig.body = JSON.stringify({
          items: payload.items,
          sections: payload.sections || ''
        });

        const responsePromise = registry.originalFetch.call(this, url, newConfig);

        if (typeof provider.onSuccess === 'function') {
          return responsePromise.then(response => {
            if (response.ok) provider.onSuccess(response);
            return response;
          });
        }

        return responsePromise;
      }

      return registry.originalFetch.call(this, url, config);
    };

    return registry;
  }

  function buildStableBundleId(mainVariantId, linkedVariantId) {
    const mainId = String(mainVariantId || '').replace(/[^a-zA-Z0-9_-]/g, '');
    const linkedId = String(linkedVariantId || '').replace(/[^a-zA-Z0-9_-]/g, '');
    if (!mainId || !linkedId) {
      return `wb_${Date.now()}`;
    }
    return `wb_${mainId}_${linkedId}`;
  }

  class BLProductCustomizer extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
      this._fetchProviderId = `customizer:${this.productId}:${Math.random().toString(36).slice(2)}`;
      this.state = {
        mode: 'none',            // 'none', 'basin', 'top'
        basin: {
          color: null,
          thickness: null,
          design: null,
          config: null,
          faucet: null,
          native: null
        },
        top: {
          product: null,
          variantId: null
        },
        furnitureSizeRaw: null
      };
      
      this.basinData = [];       // Flat array of all allowed basins
      this.topData = [];         // Array of allowed tops
      this.basePrices = {};      // Map of variantId -> price (cents)
      this.baseCompareAtPrices = {}; // Map of variantId -> compare_at_price (cents)
      this.faucetMeta = {};      // Map of normalized faucet label/handle -> presentation data
      
      this.DOM = {
        sections: {
          none: null,
          basin: this.querySelector('.bl-section-basin'),
          top: this.querySelector('.bl-section-top')
        },
        filters: {} // Populated dynamically
      };

      this.init();
    }

    getStorage() {
      try {
        return window.sessionStorage;
      } catch (error) {
        return null;
      }
    }

    getStorageKey() {
      return `bl:addon-configurator:${this.dataset.storageScope || this.productId}`;
    }

    loadPersistedAddonState() {
      const storage = this.getStorage();
      if (!storage) return null;

      try {
        const raw = storage.getItem(this.getStorageKey());
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    persistAddonState() {
      const storage = this.getStorage();
      if (!storage) return;

      try {
        storage.setItem(this.getStorageKey(), JSON.stringify({
          mode: this.state.mode,
          basin: this.state.basin,
          top: this.state.top
        }));
      } catch (error) {}
    }

    applyPersistedAddonState() {
      const persisted = this.loadPersistedAddonState();
      if (!persisted) return;

      const mode = persisted.mode || 'none';
      const basin = persisted.basin && typeof persisted.basin === 'object'
        ? Object.assign({ color: null, thickness: null, design: null, config: null, faucet: null, native: null }, persisted.basin)
        : { color: null, thickness: null, design: null, config: null, faucet: null, native: null };
      const top = persisted.top && typeof persisted.top === 'object'
        ? Object.assign({ product: null, variantId: null }, persisted.top)
        : { product: null, variantId: null };

      this.state.mode = mode;
      this.state.basin = basin;
      this.state.top = top;

      const modeButtons = this.querySelectorAll('[data-addon-mode]');
      modeButtons.forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.addonMode === mode);
      });

      if (this.DOM.sections.basin) {
        this.DOM.sections.basin.style.display = mode === 'basin' ? 'block' : 'none';
      }
      if (this.DOM.sections.top) {
        this.DOM.sections.top.style.display = mode === 'top' ? 'block' : 'none';
      }

      if (mode === 'basin') {
        this.reconcileBasinSelectionForSizeChange();
      } else if (mode === 'top') {
        this.filterTopsBySize();
        if (this.state.top.product) {
          const topBtn = this.querySelector(`[data-config-type="top_color"][data-product-id="${this.state.top.product}"]`);
          if (topBtn) {
            this.querySelectorAll('[data-config-type="top_color"]').forEach((btn) => btn.classList.remove('active'));
            topBtn.classList.add('active');
            const topProduct = this.topData.find((item) => String(item.id) === String(this.state.top.product));
            if (topProduct) {
              this.selectTopVariantForSize(topProduct);
            }
          }
        }
      }
    }

    init() {
      // Load Addon Data from <script type="application/json"> blocks
      this.basinData = JSON.parse(document.getElementById(`BL-Basin-Collections-Map-${this.productId}`)?.textContent || '[]');
      this.topData = JSON.parse(document.getElementById(`BL-Top-Products-Map-${this.productId}`)?.textContent || '[]');
      this.basePrices = JSON.parse(document.getElementById(`BL-Base-Prices-Map-${this.productId}`)?.textContent || '{}');
      this.baseCompareAtPrices = JSON.parse(document.getElementById(`BL-Base-Compare-At-Prices-Map-${this.productId}`)?.textContent || '{}');
      this.faucetMeta = this.parseFaucetMeta();

      // Detect furniture size BEFORE anything else
      this.detectFurnitureSize();
      
      // Initialize all subsystems
      this.initFurnitureSelectors();
      this.initAddonToggle();
      this.initBasinFilters();
      this.initTopSelection();
      this.initAddToCart();
      this.initDynamicCheckoutBridge();
      this.listenForSizeChanges();
      this.applyPersistedAddonState();
      this.updatePriceAndButton();

      console.log('[BL-Customizer] Initialized. Size:', this.state.furnitureSizeRaw,
                  'basinData:', this.basinData.length, 'topData:', this.topData.length);
    }

    parseFaucetMeta() {
      const script = this.querySelector('[data-metaobject-faucet]');
      if (!script) return {};

      try {
        const rawData = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(rawData)) return {};

        return rawData.reduce((acc, entry) => {
          const image = String(entry?.image || '').trim();
          const label = String(entry?.label || '').trim();
          const handle = String(entry?.handle || '').trim();
          const displayLabel = label || handle;

          [label, handle].forEach((candidate) => {
            const normalized = this.normalizeComparableValue(candidate);
            if (!normalized || acc[normalized]) return;

            acc[normalized] = {
              key: normalized,
              label: displayLabel,
              image
            };
          });

          return acc;
        }, {});
      } catch (error) {
        console.warn('[BL-Customizer] Error parsing faucet metaobject JSON', error);
        return {};
      }
    }

    parseFaucetMetaFromNode(node) {
      const script = node?.querySelector('[data-metaobject-faucet]');
      if (!script) return {};

      try {
        const rawData = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(rawData)) return {};

        return rawData.reduce((acc, entry) => {
          const image = String(entry?.image || '').trim();
          const label = String(entry?.label || '').trim();
          const handle = String(entry?.handle || '').trim();
          const displayLabel = label || handle;

          [label, handle].forEach((candidate) => {
            const normalized = this.normalizeComparableValue(candidate);
            if (!normalized || acc[normalized]) return;

            acc[normalized] = {
              key: normalized,
              label: displayLabel,
              image
            };
          });

          return acc;
        }, {});
      } catch (error) {
        console.warn('[BL-Customizer] Error parsing incoming faucet metaobject JSON', error);
        return {};
      }
    }

    resolveFaucetMeta(...candidates) {
      for (const candidate of candidates.flat()) {
        const normalized = this.normalizeComparableValue(candidate);
        if (normalized && this.faucetMeta[normalized]) {
          return this.faucetMeta[normalized];
        }
      }

      return null;
    }

    // ═══════════════════════════════════════════════════════════════
    //  SIZE DETECTION
    // ═══════════════════════════════════════════════════════════════

    getVariantPicker() {
      return document.querySelector('variant-selects, variant-radios, [data-variant-picker]');
    }

    normalizeOptionValue(value) {
      return (value || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }

    normalizeComparableValue(value) {
      return this.normalizeOptionValue(value).replace(/[^a-z0-9]/g, '');
    }

    extractNumericToken(value) {
      return this.normalizeOptionValue(value).replace(/[^\d]/g, '');
    }

    looksLikeSizeValue(value) {
      const normalized = this.normalizeOptionValue(value);
      const comparable = normalized
        .replace(/\s+/g, '')
        .replace(/[.,/x×-]/g, '')
        .replace(/"/g, '')
        .replace(/'/g, '')
        .replace(/inch|in|mm|cm|2s/g, '');
      const numeric = this.extractNumericToken(value);

      if (!normalized) return false;
      if (/^\d+([.,]\d+)?$/.test(normalized)) return true;
      if (/^\d+([.,]\d+)?\s*2s$/.test(normalized)) return true;
      if (/\d\s*(cm|mm)\b/.test(normalized)) return true;
      if (/^\d+([.,]\d+)?\s*(x|×|-|\/)\s*\d+([.,]\d+)?/.test(normalized)) return true;

      return comparable.length >= 2 && numeric.length >= 2 && !/[a-z]/.test(comparable);
    }

    getSelectedValueFromOptionBlock(block) {
      if (!block) return '';

      const selectedSpan = block.querySelector('[data-selected-value]');
      if (selectedSpan?.textContent?.trim()) return selectedSpan.textContent.trim();

      const checked = block.querySelector('input[type="radio"]:checked');
      if (checked?.value?.trim()) return checked.value.trim();

      const select = block.querySelector('select');
      if (select?.value?.trim()) return select.value.trim();

      const active = block.querySelector('.is-active, .selected, [aria-checked="true"]');
      return (active?.dataset?.value || active?.dataset?.optionValue || active?.textContent || '').trim();
    }

    getOptionValuesFromBlock(block) {
      if (!block) return [];

      const values = new Set();

      block.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.value?.trim()) values.add(input.value.trim());
      });

      block.querySelectorAll('select option').forEach((option) => {
        if (option.value?.trim()) values.add(option.value.trim());
        const optionText = option.textContent?.trim();
        if (optionText) values.add(optionText);
      });

      block.querySelectorAll('[data-value], [data-option-value]').forEach((node) => {
        const optionValue = node.dataset?.value || node.dataset?.optionValue || '';
        if (optionValue.trim()) values.add(optionValue.trim());
      });

      const selectedValue = this.getSelectedValueFromOptionBlock(block);
      if (selectedValue) values.add(selectedValue);

      return Array.from(values);
    }

    blockLooksLikeSizeOption(block) {
      const values = this.getOptionValuesFromBlock(block).filter(Boolean);
      if (!values.length) return false;

      const sizeLikeValues = values.filter((value) => this.looksLikeSizeValue(value));
      if (sizeLikeValues.length > 1) return true;
      if (sizeLikeValues.length > 0 && sizeLikeValues.length === values.length) return true;

      return this.looksLikeSizeValue(this.getSelectedValueFromOptionBlock(block));
    }

    isSameSizeValue(a, b) {
      const normalizedA = this.normalizeOptionValue(a);
      const normalizedB = this.normalizeOptionValue(b);

      if (!normalizedA || !normalizedB) return false;
      if (normalizedA === normalizedB) return true;

      const comparableA = this.normalizeComparableValue(a);
      const comparableB = this.normalizeComparableValue(b);
      if (comparableA && comparableA === comparableB) return true;

      const numericA = this.extractNumericToken(a);
      const numericB = this.extractNumericToken(b);
      return numericA.length >= 2 && numericA === numericB;
    }

    getVariantOptions(variant) {
      return [variant?.option1, variant?.option2, variant?.option3];
    }

    variantMatchesSize(variant, size = this.state.furnitureSizeRaw, optionIndex = null) {
      if (!size) return true;

      const options = this.getVariantOptions(variant);

      if (optionIndex !== null && optionIndex >= 0) {
        return this.isSameSizeValue(options[optionIndex], size);
      }

      return options.some(option => this.isSameSizeValue(option, size));
    }

    findSizeOptionIndex(variants, size = this.state.furnitureSizeRaw) {
      if (!Array.isArray(variants) || !variants.length) return -1;

      if (size) {
        for (let i = 0; i < 3; i += 1) {
          if (variants.some(variant => this.isSameSizeValue(this.getVariantOptions(variant)[i], size))) {
            return i;
          }
        }
      }

      const firstVariant = variants.find(Boolean);
      if (!firstVariant) return -1;

      for (let i = 0; i < 3; i += 1) {
        if (this.looksLikeSizeValue(this.getVariantOptions(firstVariant)[i])) {
          return i;
        }
      }

      return -1;
    }

    findVariantForSize(variants, size = this.state.furnitureSizeRaw, optionIndex = null) {
      if (!Array.isArray(variants) || !variants.length) return null;
      if (!size) return variants[0];

      return variants.find(variant => this.variantMatchesSize(variant, size, optionIndex))
        || variants.find(variant => this.variantMatchesSize(variant, size));
    }

    syncFurnitureSize(force = false) {
      const oldSize = this.state.furnitureSizeRaw;
      this.detectFurnitureSize();

      if (force || this.state.furnitureSizeRaw !== oldSize) {
        console.log('[BL-Customizer] Size changed:', oldSize, '->', this.state.furnitureSizeRaw);
        this.onSizeChanged();
        return true;
      }

      this.updatePriceAndButton();

      return false;
    }

    detectFurnitureSize() {
      this.state.furnitureSizeRaw = null;

      const picker = this.getVariantPicker();
      
      if (picker) {
          const allBlocks = picker.querySelectorAll('fieldset, .product-form__input, .variant-picker__option');
          for (const block of allBlocks) {
              if (!this.blockLooksLikeSizeOption(block)) continue;

              this.state.furnitureSizeRaw = this.getSelectedValueFromOptionBlock(block);
              if (this.state.furnitureSizeRaw) break;
          }
      }
      
      // Fallback: form inputs
      if (!this.state.furnitureSizeRaw) {
          const form = document.querySelector('form[action*="/cart/add"]');
          if (form) {
              const widthSelect = Array.from(form.querySelectorAll('select, input[type="radio"]:checked')).find(el => {
                return this.looksLikeSizeValue(el.value || el.dataset?.value || '');
              });
              if (widthSelect) {
                  this.state.furnitureSizeRaw = widthSelect.value.trim();
              }
          }
      }

      // Fallback: any [data-selected-value] with a numeric value
      if (!this.state.furnitureSizeRaw) {
          document.querySelectorAll('[data-selected-value]').forEach(el => {
              const text = el.textContent.trim();
              if (this.looksLikeSizeValue(text)) {
                  this.state.furnitureSizeRaw = text;
              }
          });
      }
      
      console.log('[BL-Customizer] Detected furniture size:', this.state.furnitureSizeRaw);
    }
    
    listenForSizeChanges() {
      const scheduleSync = (force = false, delay = 40) => {
        clearTimeout(this._sizeSyncTimer);
        this._sizeSyncTimer = setTimeout(() => this.syncFurnitureSize(force), delay);
      };

      this._scheduleSizeSync = scheduleSync;
      this.bindVariantPickerEvents();

      this._boundVariantChanged = () => scheduleSync(false, 0);
      document.addEventListener('variant:changed', this._boundVariantChanged);
    }

    bindVariantPickerEvents() {
      const picker = this.getVariantPicker();
      if (!picker) return;

      if (!this._boundPickerSizeSync) {
        this._boundPickerSizeSync = () => {
          if (typeof this._scheduleSizeSync === 'function') {
            this._scheduleSizeSync(false, 40);
          }
        };
      }

      if (this._variantPicker && this._variantPicker !== picker && this._boundPickerSizeSync) {
        this._variantPicker.removeEventListener('click', this._boundPickerSizeSync);
        this._variantPicker.removeEventListener('change', this._boundPickerSizeSync);
      }

      this._variantPicker = picker;
      this._variantPicker.addEventListener('click', this._boundPickerSizeSync);
      this._variantPicker.addEventListener('change', this._boundPickerSizeSync);
    }

    readJsonScriptFromNode(node, prefix, fallback = {}) {
      const script = node?.querySelector(`[id^="${prefix}"]`);
      if (!script) return fallback;

      try {
        return JSON.parse(script.textContent || JSON.stringify(fallback));
      } catch (error) {
        return fallback;
      }
    }

    updateProductContextFromNode(nextCustomizerNode) {
      const incomingCustomizer = nextCustomizerNode?.matches?.('bl-product-customizer')
        ? nextCustomizerNode
        : nextCustomizerNode?.querySelector?.('bl-product-customizer');

      if (!incomingCustomizer) return false;

      const nextProductId = incomingCustomizer.dataset.productId || this.productId;
      this.productId = nextProductId;
      this.dataset.productId = nextProductId;

      this.basinData = this.readJsonScriptFromNode(incomingCustomizer, 'BL-Basin-Collections-Map-', []);
      this.topData = this.readJsonScriptFromNode(incomingCustomizer, 'BL-Top-Products-Map-', []);
      this.basePrices = this.readJsonScriptFromNode(incomingCustomizer, 'BL-Base-Prices-Map-', {});
      this.baseCompareAtPrices = this.readJsonScriptFromNode(incomingCustomizer, 'BL-Base-Compare-At-Prices-Map-', {});
      this.faucetMeta = this.parseFaucetMetaFromNode(incomingCustomizer);

      this.bindVariantPickerEvents();
      this.detectFurnitureSize();

      if (this.state.mode === 'basin') {
        this.reconcileBasinSelectionForSizeChange();
      } else if (this.state.mode === 'top') {
        this.filterTopsBySize();
        if (this.state.top.product) {
          const topProduct = this.topData.find((item) => String(item.id) === String(this.state.top.product));
          if (topProduct) {
            this.selectTopVariantForSize(topProduct);
          }
        }
      }

      this.updatePriceAndButton();
      return true;
    }

    onSizeChanged() {
        this.filterTopsBySize();

        if (this.state.mode === 'basin') {
            this.reconcileBasinSelectionForSizeChange();
        } else if (this.state.mode === 'top') {
            // Re-select the current top's variant for the new size
            if (this.state.top.product) {
                const topProduct = this.topData.find(t => t.id == this.state.top.product);
                if (topProduct) {
                    this.selectTopVariantForSize(topProduct);
                }
            }
        }

        this.updatePriceAndButton();
    }

    // ═══════════════════════════════════════════════════════════════
    //  FURNITURE SELECTORS (handled by grouping block inline script)
    // ═══════════════════════════════════════════════════════════════

    initFurnitureSelectors() {
      // The redirect logic for furniture base swatches is handled inline 
      // by BL-product-grouping-based.liquid, so nothing needed here.
    }

    // ═══════════════════════════════════════════════════════════════
    //  ADDON 3-WAY TOGGLE
    // ═══════════════════════════════════════════════════════════════

    initAddonToggle() {
      const toggleGroup = this.querySelector('[data-config-group="addon_mode"]');
      if (!toggleGroup) return;

      const buttons = toggleGroup.querySelectorAll('[data-addon-mode]');
      
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const mode = btn.dataset.addonMode;
          
          buttons.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          
          this.state.mode = mode;

          if (mode === 'basin') {
              this.state.basin = { color: null, thickness: null, design: null, config: null, faucet: null, native: null };
              this.hideFilters(['color', 'thickness', 'design', 'config', 'faucet', 'native']);
          } else {
              this.state.basin = { color: null, thickness: null, design: null, config: null, faucet: null, native: null };
          }

          if (mode !== 'top') {
              this.state.top = { product: null, variantId: null };
          }
          
          // Show/hide sections
          if (this.DOM.sections.basin) {
              this.DOM.sections.basin.style.display = mode === 'basin' ? 'block' : 'none';
          }
          if (this.DOM.sections.top) {
              this.DOM.sections.top.style.display = mode === 'top' ? 'block' : 'none';
          }
          
          // When activating basin mode, start the cascade 
          if (mode === 'basin') {
              this.renderBasinColors();
          } else if (mode === 'top') {
              this.filterTopsBySize();
          }
          
          this.updatePriceAndButton();
          console.log('[BL-Customizer] Mode switched to:', mode);
        });
      });
    }

    // ═══════════════════════════════════════════════════════════════
    //  BASIN CASCADE FILTERS
    // ═══════════════════════════════════════════════════════════════

    initBasinFilters() {
      // Cache DOM references for each filter group
      ['color', 'thickness', 'design', 'config', 'faucet', 'native'].forEach(key => {
        const container = this.querySelector(`[data-filter-group="${key}"]`);
        if (container) {
          this.DOM.filters[key] = {
            container,
            label: container.querySelector('[data-selected-value]'),
            optionsWrap: container.querySelector('.swatches-container') || 
                         container.querySelector('.bl-design-options') ||
                         container.querySelector('.bl-color-options') ||
                         container.querySelector('.bl-thickness-options') || 
                         container.querySelector('.bl-config-options') || 
                         container.querySelector('.bl-faucet-options') || container
          };
        }
      });
    }

    // Helper: check if a basin has a variant matching the current furniture size
    basinMatchesSize(basin) {
        const currentSize = this.state.furnitureSizeRaw;
        if (!currentSize) return true;
        return !!this.findVariantForSize(basin.variants, currentSize);
    }

    // Helper: get the price of the size-matched variant for a basin
    getSizePriceForBasin(basin) {
        const currentSize = this.state.furnitureSizeRaw;
        if (currentSize) {
            const matched = this.findVariantForSize(basin.variants, currentSize);
            if (matched) return matched.price;
        }
        return basin.price; // fallback
    }

    getFilteredBasins(filters = {}) {
      return this.basinData.filter((basin) => {
        if (filters.color && basin.color_handle !== filters.color) return false;
        if (filters.thickness && basin.thickness_handle !== filters.thickness) return false;
        if (filters.design && basin.design_handle !== filters.design) return false;
        if (filters.config && basin.config_handle !== filters.config) return false;
        if (filters.faucet && basin.faucet_handle !== filters.faucet) return false;
        if (!this.basinMatchesSize(basin)) return false;
        return true;
      });
    }

    getSelectedBasinRecord() {
      const b = this.state.basin;
      const matches = this.getFilteredBasins({
        color: b.color,
        thickness: b.thickness,
        design: b.design,
        config: b.config,
        faucet: b.faucet
      });

      return matches[0] || null;
    }

    getAvailableBasinColors() {
      const colors = [];
      const colorMap = new Map();

      this.basinData.forEach((basin) => {
        if (colorMap.has(basin.color_handle)) return;
        if (!this.basinMatchesSize(basin)) return;

        colorMap.set(basin.color_handle, true);
        colors.push({
          handle: basin.color_handle,
          name: basin.color_name,
          code: basin.color_code,
          image: basin.color_image
        });
      });

      return colors;
    }

    getAvailableBasinThicknesses(color = this.state.basin.color) {
      if (!color) return [];

      const thicknesses = [];
      const thickMap = new Map();

      this.basinData.forEach((basin) => {
        if (basin.color_handle !== color) return;
        if (!this.basinMatchesSize(basin)) return;
        if (thickMap.has(basin.thickness_handle)) return;

        thickMap.set(basin.thickness_handle, true);
        thicknesses.push({
          handle: basin.thickness_handle,
          name: basin.thickness_name,
          image: basin.thickness_image || ''
        });
      });

      return thicknesses;
    }

    getAvailableBasinDesigns(color = this.state.basin.color, thickness = this.state.basin.thickness) {
      const filteredBasins = this.getFilteredBasins({ color, thickness });
      const designs = [];
      const designMap = new Map();

      filteredBasins.forEach((basin) => {
        if (designMap.has(basin.design_handle)) return;
        designMap.set(basin.design_handle, true);

        const sizePrice = this.getSizePriceForBasin(basin);
        designs.push(Object.assign({}, basin, {
          _sizePrice: sizePrice,
          price_formatted: '+' + this.formatMoney(sizePrice)
        }));
      });

      return designs;
    }

    getAvailableBasinConfigs(color = this.state.basin.color, thickness = this.state.basin.thickness, design = this.state.basin.design) {
      const filteredBasins = this.getFilteredBasins({ color, thickness, design });
      const validConfigs = [];
      const configMap = new Map();
      const currentSize = this.state.furnitureSizeRaw;

      filteredBasins.forEach((basin) => {
        const configKey = basin.config_handle || '';
        if (configMap.has(configKey)) return;

        configMap.set(configKey, true);
        validConfigs.push({
          handle: configKey,
          name: basin.config_name || basin.product_title,
          image: basin.config_image || '',
          available: currentSize ? !!this.findVariantForSize(basin.variants, currentSize) : true
        });
      });

      return validConfigs;
    }

    getAvailableBasinFaucets(color = this.state.basin.color, thickness = this.state.basin.thickness, design = this.state.basin.design, config = this.state.basin.config) {
      const filteredBasins = this.getFilteredBasins({ color, thickness, design, config });
      const faucetOptions = [];
      const faucetMap = new Map();

      filteredBasins.forEach((basin) => {
        const displayLabel = basin.faucet_name || basin.product_title;
        const labelKey = basin.faucet_handle || this.normalizeComparableValue(displayLabel) || '';
        if (faucetMap.has(labelKey)) return;

        faucetMap.set(labelKey, true);
        faucetOptions.push({
          handle: basin.faucet_handle || '',
          name: displayLabel,
          image: basin.faucet_image || ''
        });
      });

      return faucetOptions;
    }

    getAvailableNativeOptions(finalBasin = this.getSelectedBasinRecord()) {
      if (!finalBasin) {
        return { nativeOptions: [], sizeMatchedVariants: [] };
      }

      const currentSize = this.state.furnitureSizeRaw;
      const sizeOptionIndex = this.findSizeOptionIndex(finalBasin.variants, currentSize);
      let sizeMatchedVariants = finalBasin.variants;

      if (currentSize) {
        sizeMatchedVariants = finalBasin.variants.filter((variant) =>
          this.variantMatchesSize(variant, currentSize, sizeOptionIndex)
        );
      }

      const nativeOptions = [];
      const nativeMap = new Map();

      sizeMatchedVariants.forEach((variant) => {
        const nonSizeOpts = [];
        for (let i = 0; i < 3; i += 1) {
          if (i === sizeOptionIndex) continue;
          const val = variant[`option${i + 1}`];
          if (val && val.toLowerCase() !== 'default title') {
            nonSizeOpts.push(val);
          }
        }

        const fallbackLabel = nonSizeOpts.join(' / ') || variant.title;
        const metaEntry = this.resolveFaucetMeta(nonSizeOpts, fallbackLabel);
        const displayLabel = metaEntry?.label || fallbackLabel;
        const labelKey = metaEntry?.key || this.normalizeComparableValue(displayLabel) || String(variant.id);

        if (nativeMap.has(labelKey)) return;

        nativeMap.set(labelKey, true);
        nativeOptions.push({
          handle: variant.id.toString(),
          name: displayLabel,
          image: metaEntry?.image || ''
        });
      });

      return { nativeOptions, sizeMatchedVariants };
    }

    setActiveOptionUI(key, value) {
      const wrap = this.DOM.filters[key]?.optionsWrap;
      if (!wrap) return;

      const options = Array.from(wrap.querySelectorAll('[data-value]'));
      let hasSelection = false;

      options.forEach((el) => {
        const isActive = value !== null && value !== undefined && el.dataset.value === String(value);
        el.classList.toggle('active', isActive);
        el.classList.toggle('is-selected', isActive);
        hasSelection = hasSelection || isActive;
      });

      wrap.classList.toggle('has-selection', hasSelection);
    }

    syncBasinUIFromState() {
      const basin = this.state.basin;

      this.setActiveOptionUI('color', basin.color);
      this.setActiveOptionUI('thickness', basin.thickness);
      this.setActiveOptionUI('config', basin.config);
      this.setActiveOptionUI('faucet', basin.faucet);
      this.setActiveOptionUI('native', basin.native);

      const designWrap = this.querySelector('.bl-design-options');
      const designContainer = this.DOM.filters.design?.container;
      const pillRow = designContainer?.querySelector('[data-design-pill-row]');

      if (designWrap) {
        let hasSelection = false;
        designWrap.querySelectorAll('.washbasin-selector__item').forEach((card) => {
          const isActive = basin.design && card.dataset.value === basin.design;
          card.classList.toggle('is-active', isActive);
          hasSelection = hasSelection || isActive;
        });
        designWrap.classList.toggle('has-selection', hasSelection);
      }

      if (pillRow) {
        let hasSelection = false;
        pillRow.querySelectorAll('[data-design-pill]').forEach((pill) => {
          const isActive = basin.design && pill.dataset.value === basin.design;
          pill.classList.toggle('active', isActive);
          hasSelection = hasSelection || isActive;
        });
        pillRow.classList.toggle('has-selection', hasSelection);
      }
    }

    reconcileBasinSelectionForSizeChange() {
      const previous = Object.assign({}, this.state.basin);
      const next = { color: null, thickness: null, design: null, config: null, faucet: null, native: null };

      this.hideFilters(['color', 'thickness', 'design', 'config', 'faucet', 'native']);

      const colors = this.getAvailableBasinColors();
      if (!colors.length) {
        this.state.basin = next;
        this.renderBasinColors();
        return;
      }

      next.color = colors.some((item) => item.handle === previous.color)
        ? previous.color
        : colors[0].handle;
      this.state.basin = next;
      this.renderBasinColors();

      const selectedColor = colors.find((item) => item.handle === next.color);
      if (selectedColor && this.DOM.filters.color?.label) {
        this.DOM.filters.color.label.textContent = selectedColor.name;
      }
      this.setActiveOptionUI('color', next.color);

      const thicknesses = this.getAvailableBasinThicknesses(next.color);
      if (!thicknesses.length || !thicknesses.some((item) => item.handle === previous.thickness)) {
        this.syncBasinUIFromState();
        return;
      }

      next.thickness = previous.thickness;
      this.state.basin = next;
      this.renderBasinThicknesses();

      const selectedThickness = thicknesses.find((item) => item.handle === next.thickness);
      if (selectedThickness && this.DOM.filters.thickness?.label) {
        this.DOM.filters.thickness.label.textContent = selectedThickness.name;
      }
      this.setActiveOptionUI('thickness', next.thickness);

      const designs = this.getAvailableBasinDesigns(next.color, next.thickness);
      if (!designs.length || !designs.some((item) => item.design_handle === previous.design)) {
        this.syncBasinUIFromState();
        return;
      }

      next.design = previous.design;
      this.state.basin = next;
      this.renderBasinDesigns();

      const selectedDesign = designs.find((item) => item.design_handle === next.design);
      if (selectedDesign && this.DOM.filters.design?.label) {
        this.DOM.filters.design.label.textContent = selectedDesign.design_name;
      }
      this.syncBasinUIFromState();

      const configs = this.getAvailableBasinConfigs(next.color, next.thickness, next.design);
      const configHandles = configs.filter((item) => item.available).map((item) => item.handle);
      if (!configs.length) {
        this.syncBasinUIFromState();
        return;
      }

      if (configs.length === 1 && !configs[0].handle) {
        next.config = '';
      } else if (configHandles.includes(previous.config)) {
        next.config = previous.config;
      } else {
        this.state.basin = next;
        this.renderBasinConfigs();
        this.syncBasinUIFromState();
        return;
      }

      this.state.basin = next;
      this.renderBasinConfigs();
      if (this.DOM.filters.config?.label && next.config !== null) {
        const selectedConfig = configs.find((item) => item.handle === next.config);
        if (selectedConfig) this.DOM.filters.config.label.textContent = selectedConfig.name;
      }
      this.setActiveOptionUI('config', next.config);

      const faucets = this.getAvailableBasinFaucets(next.color, next.thickness, next.design, next.config);
      if (!faucets.length) {
        this.syncBasinUIFromState();
        return;
      }

      if (faucets.length === 1 && !faucets[0].handle) {
        next.faucet = null;
      } else if (faucets.some((item) => item.handle === previous.faucet)) {
        next.faucet = previous.faucet;
      } else {
        this.state.basin = next;
        this.renderFaucetHoles();
        this.syncBasinUIFromState();
        return;
      }

      this.state.basin = next;
      this.renderFaucetHoles();
      if (this.DOM.filters.faucet?.label && next.faucet !== null) {
        const selectedFaucet = faucets.find((item) => item.handle === next.faucet);
        if (selectedFaucet) this.DOM.filters.faucet.label.textContent = selectedFaucet.name;
      }
      this.setActiveOptionUI('faucet', next.faucet);

      const finalBasin = this.getSelectedBasinRecord();
      const { nativeOptions, sizeMatchedVariants } = this.getAvailableNativeOptions(finalBasin);
      if (!sizeMatchedVariants.length) {
        this.renderNativeOptions();
        this.syncBasinUIFromState();
        return;
      }

      if (nativeOptions.length <= 1) {
        next.native = sizeMatchedVariants[0]?.id?.toString() || null;
      } else if (nativeOptions.some((item) => item.handle === previous.native)) {
        next.native = previous.native;
      } else {
        this.state.basin = next;
        this.renderNativeOptions();
        this.syncBasinUIFromState();
        return;
      }

      this.state.basin = next;
      this.renderNativeOptions();
      if (this.DOM.filters.native?.label && next.native !== null) {
        const selectedNative = nativeOptions.find((item) => item.handle === next.native);
        if (selectedNative) this.DOM.filters.native.label.textContent = selectedNative.name;
      }
      this.setActiveOptionUI('native', next.native);
      this.syncBasinUIFromState();
    }

    // Step 1: Colors (filtered by size availability)
    renderBasinColors() {
      const colors = this.getAvailableBasinColors();

      this.populateSwatches('color', colors, (val, name) => {
         this.state.basin.color = val;
         if (this.DOM.filters.color.label) this.DOM.filters.color.label.textContent = name;
         
         // Reset downstream
         this.state.basin.thickness = null;
         this.state.basin.design = null;
         this.state.basin.config = null;
         this.state.basin.faucet = null;
         this.state.basin.native = null;
         
         this.hideFilters(['design', 'config', 'faucet', 'native']);
         this.renderBasinThicknesses();
         this.updatePriceAndButton();
      });

      if (!this.DOM.filters.color) return;

      this.DOM.filters.color.container.style.display = colors.length ? 'block' : 'none';
      this.hideFilters(['thickness', 'design', 'config', 'faucet', 'native']);

      if (!colors.length) return;

      const selectedColor = colors.find((item) => item.handle === this.state.basin.color) || colors[0];
      this.state.basin.color = selectedColor.handle;
      if (this.DOM.filters.color.label) this.DOM.filters.color.label.textContent = selectedColor.name;

      const firstColorEl = Array.from(this.DOM.filters.color.optionsWrap.querySelectorAll('[data-value]'))
        .find((el) => el.dataset.value === selectedColor.handle);
      if (firstColorEl) {
        firstColorEl.classList.add('active');
        this.DOM.filters.color.optionsWrap.classList.add('has-selection');
      }

      this.renderBasinThicknesses();
    }

    // Step 2: Thicknesses (filtered by color + size)
    renderBasinThicknesses() {
      const thicknesses = this.getAvailableBasinThicknesses();

      if (this.DOM.filters.thickness) {
          this.DOM.filters.thickness.container.style.display = 'block';
          if (this.DOM.filters.thickness.label) {
              this.DOM.filters.thickness.label.textContent = '';
          }
          this.populateTextButtons('thickness', thicknesses, (val, name) => {
             this.state.basin.thickness = val;
             if (this.DOM.filters.thickness.label) this.DOM.filters.thickness.label.textContent = name;
             
             // Reset downstream design selection (but keep design section visible)
             this.state.basin.design = null;
             this.state.basin.config = null;
             this.state.basin.faucet = null;
             this.state.basin.native = null;
             
             this.hideFilters(['config', 'faucet', 'native']);
             this.renderBasinDesigns();
             this.updatePriceAndButton();
          });
      }
    }

    // Step 3: Designs / Models.
    // When thickness is null: shows ALL designs for the selected color (unfiltered).
    // When thickness is set: filters designs by color + thickness.
    renderBasinDesigns() {
      const designs = this.getAvailableBasinDesigns();

      if (this.DOM.filters.design) {
          this.DOM.filters.design.container.style.display = 'block';
          if (this.DOM.filters.design.label) {
              this.DOM.filters.design.label.textContent = '';
          }
          this.populateDesignCards(designs, (val, name) => {
             this.state.basin.design = val;
             if (this.DOM.filters.design.label) this.DOM.filters.design.label.textContent = name;
             
             // Reset downstream
             this.state.basin.config = null;
             this.state.basin.faucet = null;
             this.state.basin.native = null;
             
             this.hideFilters(['faucet', 'native']);
             this.renderBasinConfigs();
             this.updatePriceAndButton(); // Progressive price update
          });
      }

      this.updatePriceAndButton(); // Progressive price after designs render
    }

    // Step 4: Config / Senos (filtered by color + thickness + design + size)
    renderBasinConfigs() {
      const filteredBasins = this.getFilteredBasins({
        color: this.state.basin.color,
        thickness: this.state.basin.thickness,
        design: this.state.basin.design
      });
      
      if (!filteredBasins.length) return;
      const validConfigs = this.getAvailableBasinConfigs();

      if (!validConfigs.length || (validConfigs.length === 1 && !validConfigs[0].handle)) {
          this.hideFilters(['config']);
          this.state.basin.config = validConfigs[0]?.handle || '';
          this.renderFaucetHoles();
          return;
      }

      if (this.DOM.filters.config) {
          this.DOM.filters.config.container.style.display = 'block';
          this.populateConfigButtons(validConfigs, (val, name) => {
             this.state.basin.config = val;
             if (this.DOM.filters.config.label) this.DOM.filters.config.label.textContent = name;
             this.state.basin.faucet = null;
             this.state.basin.native = null;
             this.renderFaucetHoles();
             this.updatePriceAndButton(); // Progressive price update
          });

          if (validConfigs.length === 1) {
            this.state.basin.config = validConfigs[0].handle;
            if (this.DOM.filters.config.label) this.DOM.filters.config.label.textContent = validConfigs[0].name;
            const firstOption = this.DOM.filters.config.optionsWrap.querySelector('a');
            if (firstOption) firstOption.classList.add('is-selected', 'active');
            this.renderFaucetHoles();
          }
      }
    }

    // Step 5: Variant 4th
    renderFaucetHoles() {
      const filteredBasins = this.getFilteredBasins({
        color: this.state.basin.color,
        thickness: this.state.basin.thickness,
        design: this.state.basin.design,
        config: this.state.basin.config
      });

      if (!filteredBasins.length) {
          this.hideFilters(['faucet']);
          this.state.basin.faucet = null;
          return;
      }
      const faucetOptions = this.getAvailableBasinFaucets();

      if (!faucetOptions.length || (faucetOptions.length === 1 && !faucetOptions[0].handle)) {
          this.state.basin.faucet = null;
          this.hideFilters(['faucet']);
          this.renderNativeOptions();
          this.updatePriceAndButton();
          return;
      }

      if (this.DOM.filters.faucet) {
          this.DOM.filters.faucet.container.style.display = 'block';
          this.populateTextButtons('faucet', faucetOptions, (val, name) => {
             this.state.basin.faucet = val;
             if (this.DOM.filters.faucet.label) this.DOM.filters.faucet.label.textContent = name;
             this.state.basin.native = null;
             this.renderNativeOptions();
             this.updatePriceAndButton();
          });

          if (faucetOptions.length === 1) {
            this.state.basin.faucet = faucetOptions[0].handle;
            if (this.DOM.filters.faucet.label) this.DOM.filters.faucet.label.textContent = faucetOptions[0].name;
            const firstOption = this.DOM.filters.faucet.optionsWrap.querySelector('a');
            if (firstOption) firstOption.classList.add('is-selected', 'active');
            this.renderNativeOptions();
          }
      }
    }

    // Step 6: Native product variant option (excluding size)
    renderNativeOptions() {
      const finalBasin = this.getSelectedBasinRecord();
      if (!finalBasin) return;
      const { nativeOptions, sizeMatchedVariants } = this.getAvailableNativeOptions(finalBasin);

      if (!sizeMatchedVariants.length) {
        this.hideFilters(['native']);
        this.state.basin.native = null;
        return;
      }

      if (!this.DOM.filters.native) return;

      if (nativeOptions.length <= 1) {
        this.hideFilters(['native']);
        this.state.basin.native = sizeMatchedVariants[0]?.id?.toString() || null;
        return;
      }

      this.DOM.filters.native.container.style.display = 'block';
      this.populateTextButtons('native', nativeOptions, (val, name) => {
        this.state.basin.native = val;
        if (this.DOM.filters.native.label) this.DOM.filters.native.label.textContent = name;
        this.updatePriceAndButton();
      });

      if (this.DOM.filters.native.label) {
        this.DOM.filters.native.label.textContent = '';
      }

      if (nativeOptions.length === 1) {
        this.state.basin.native = nativeOptions[0].handle;
        if (this.DOM.filters.native.label) this.DOM.filters.native.label.textContent = nativeOptions[0].name;
        const firstOption = this.DOM.filters.native.optionsWrap.querySelector('a');
        if (firstOption) firstOption.classList.add('is-selected', 'active');
      }
    }

    // ═══════════════════════════════════════════════════════════════
    //  TOP SELECTION
    // ═══════════════════════════════════════════════════════════════

    initTopSelection() {
      this.filterTopsBySize();
      
      this.querySelectorAll('[data-config-type="top_color"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.querySelectorAll('[data-config-type="top_color"]').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          
          const productId = btn.dataset.productId;
          const topProduct = this.topData.find(t => t.id == productId);
          
          if (topProduct) {
             this.state.top.product = productId;
             this.selectTopVariantForSize(topProduct);
          }
          
          this.updatePriceAndButton();
        });
      });
    }

    selectTopVariantForSize(topProduct) {
        const currentSize = this.state.furnitureSizeRaw;
        let matchedVariant = null;

        if (currentSize) {
            matchedVariant = this.findVariantForSize(topProduct.variants, currentSize);
        }
        this.state.top.variantId = matchedVariant ? matchedVariant.id : topProduct.variants[0].id;
    }

    filterTopsBySize() {
        const currentSize = this.state.furnitureSizeRaw;
        const topButtons = this.querySelectorAll('[data-config-type="top_color"]');
        
        let hasVisibleTops = false;
        topButtons.forEach(btn => {
            const productId = btn.dataset.productId;
            const topProduct = this.topData.find(t => t.id == productId);
            const parent = btn.closest('.bl-swatch-item') || btn;
            
            if (topProduct) {
                  let matchedVariant = null;
                  if (currentSize) {
                      matchedVariant = this.findVariantForSize(topProduct.variants, currentSize);
                  } else {
                      matchedVariant = topProduct.variants[0];
                  }
                  
                  if (matchedVariant) {
                      parent.style.display = 'inline-flex';
                      hasVisibleTops = true;
                  } else {
                      parent.style.display = 'none';
                      if (btn.classList.contains('active')) {
                          btn.classList.remove('active');
                          this.state.top.product = null;
                          this.state.top.variantId = null;
                      }
                  }
            }
        });
        
        const topSectionBtn = this.querySelector('[data-addon-mode="top"]');
        if (topSectionBtn) {
            topSectionBtn.style.display = hasVisibleTops ? 'inline-flex' : 'none';
            if (!hasVisibleTops && this.state.mode === 'top') {
                this.state.mode = 'none';
                this.querySelector('[data-addon-mode="none"]')?.classList.add('active');
                if (this.DOM.sections.top) this.DOM.sections.top.style.display = 'none';
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════
    //  RENDER HELPERS
    // ═══════════════════════════════════════════════════════════════
    
    hideFilters(keys) {
      keys.forEach(k => {
         if (this.DOM.filters[k] && this.DOM.filters[k].container) {
            this.DOM.filters[k].container.style.display = 'none';
            if (this.DOM.filters[k].label) {
               this.DOM.filters[k].label.textContent = '';
            }
         }
      });
    }

    // Silent auto-select: visually marks the first swatch/pill as active WITHOUT triggering click
    silentSelectFirst(key, firstItem, callback) {
        const wrap = this.DOM.filters[key]?.optionsWrap;
        if (!wrap) return;
        const firstEl = wrap.querySelector('a');
        if (firstEl) {
            firstEl.classList.add('active');
            callback();
        }
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    buildOptionMediaMarkup(item) {
      const image = String(item?.image || '').trim();
      if (!image) return '';

      if (/\.svg(?:\?|$)/i.test(image)) {
        return `<span class="bl-option-icon-svg" style="--svg-url: url('${this.escapeHtml(image)}');" aria-hidden="true"></span>`;
      }

      return `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(item?.name || '')}" class="bl-option-img" loading="lazy">`;
    }

    populateSwatches(key, items, onClick) {
      const wrap = this.DOM.filters[key].optionsWrap;
      wrap.innerHTML = '';
      wrap.classList.remove('has-selection');
      
      items.forEach(item => {
        let style = '';
        if (item.image) {
           style = `background-image: url(${item.image}); background-size: cover;`;
        } else if (item.code) {
           style = `background-color: ${item.code};`;
        }
        
        const a = document.createElement('a');
        a.href = "javascript:void(0);";
        a.className = "swatch-color block relative";
        a.dataset.value = item.handle;
        a.title = item.name;
        a.style = style;
        
        a.innerHTML = `<span class="visually-hidden">${item.name}</span>`;
        
        a.addEventListener('click', (e) => {
           e.preventDefault();
           wrap.querySelectorAll('a').forEach(el => el.classList.remove('active'));
           a.classList.add('active');
           wrap.classList.add('has-selection');
           onClick(item.handle, item.name);
        });
        
        wrap.appendChild(a);
      });
    }

    populateTextButtons(key, items, onClick) {
      const wrap = this.DOM.filters[key].optionsWrap;
      const filterContainer = this.DOM.filters[key].container;
      const iconOnly = String(filterContainer?.dataset.showText) === 'false';
      wrap.innerHTML = '';
      wrap.classList.remove('has-selection');
      
      items.forEach(item => {
        const a = document.createElement('a');
        a.href = "javascript:void(0);";
        a.className = "bl-option-btn swatch-pill";
        a.dataset.value = item.handle;
        a.title = item.name;

        if (item.image) {
           a.classList.add('swatch-pill--with-icon');
           if (iconOnly) a.classList.add('swatch-pill--icon-only');
        }

        a.innerHTML = `${this.buildOptionMediaMarkup(item)}<span class="bl-option-text">${this.escapeHtml(item.name)}</span>`;
        
        a.addEventListener('click', (e) => {
           e.preventDefault();
           wrap.querySelectorAll('a').forEach(el => el.classList.remove('is-selected', 'active'));
           a.classList.add('is-selected', 'active');
           wrap.classList.add('has-selection');
           onClick(item.handle, item.name);
        });
        
        wrap.appendChild(a);
      });
    }

    populateConfigButtons(items, onClick) {
      const wrap = this.DOM.filters.config.optionsWrap;
      const filterContainer = this.DOM.filters.config.container;
      const iconOnly = String(filterContainer?.dataset.showText) === 'false';
      wrap.innerHTML = '';
      wrap.classList.remove('has-selection');
      
      items.forEach(item => {
        const a = document.createElement('a');
        a.href = "javascript:void(0);";
        a.className = "bl-option-btn swatch-pill";
        a.dataset.value = item.handle;
        a.title = item.name;

        if (item.image) {
            a.classList.add('swatch-pill--with-icon');
            if (iconOnly) a.classList.add('swatch-pill--icon-only');
        }

        a.innerHTML = `${this.buildOptionMediaMarkup(item)}<span class="bl-option-text">${this.escapeHtml(item.name)}</span>`;
        
        if (!item.available) {
            a.classList.add('bl-unavailable');
            a.style.opacity = '0.35';
            a.style.pointerEvents = 'none';
        }
        
        a.addEventListener('click', (e) => {
           e.preventDefault();
           if (!item.available) return;
           wrap.querySelectorAll('a').forEach(el => el.classList.remove('is-selected', 'active'));
           a.classList.add('is-selected', 'active');
           wrap.classList.add('has-selection');
           onClick(item.handle, item.name);
        });
        
        wrap.appendChild(a);
      });
    }

    updateDesignSliderProgress(slider) {
      if (!slider) return;

      const progressEl = slider.closest('.washbasin-selector__slider-wrapper')
        ?.querySelector('.washbasin-selector__progress');
      if (!progressEl) return;

      const maxScroll = slider.scrollWidth - slider.clientWidth;
      if (maxScroll <= 0) {
        progressEl.style.width = '100%';
        return;
      }

      const scrollRatio = slider.scrollLeft / maxScroll;
      const percent = Math.max(10, Math.min(100, scrollRatio * 100));
      progressEl.style.width = `${percent}%`;
    }

    getCenteredDesignCard(slider) {
      if (!slider) return null;

      const cards = Array.from(slider.querySelectorAll('.washbasin-selector__item'));
      if (!cards.length) return null;

      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const edgeThreshold = 24;

      if (slider.scrollLeft <= edgeThreshold) return cards[0];
      if (maxScroll - slider.scrollLeft <= edgeThreshold) return cards[cards.length - 1];

      const sliderRect = slider.getBoundingClientRect();
      const center = sliderRect.left + (sliderRect.width / 2);
      let closestCard = cards[0];
      let minDistance = Infinity;

      cards.forEach(card => {
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.left + (rect.width / 2);
        const distance = Math.abs(center - cardCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestCard = card;
        }
      });

      return closestCard;
    }

    scrollDesignCardIntoView(slider, card, smooth = true) {
      if (!slider || !card) return;

      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const centeredLeft = card.offsetLeft - ((slider.clientWidth - card.offsetWidth) / 2);
      const targetLeft = Math.min(maxScroll, Math.max(0, centeredLeft));

      this._designInternalScroll = true;
      slider.scrollTo({
        left: targetLeft,
        behavior: smooth ? 'smooth' : 'auto'
      });

      clearTimeout(this._designInternalScrollTimer);
      this._designInternalScrollTimer = setTimeout(() => {
        this._designInternalScroll = false;
      }, smooth ? 600 : 50);
    }

    populateDesignCards(basins, onClick) {
      const wrap = this.querySelector('.bl-design-options');
      const designContainer = this.DOM.filters.design?.container;
      const pillRow = designContainer?.querySelector('[data-design-pill-row]');
      if (!wrap || !designContainer || !pillRow) return;

      wrap.innerHTML = '';
      wrap.classList.remove('has-selection');
      pillRow.innerHTML = '';
      pillRow.classList.remove('has-selection');

      const selectDesignValue = (value, options = {}) => {
        const { silent = false, skipScroll = false } = options;
        const selectedBasin = basins.find(basin => basin.design_handle === value);
        const selectedCard = Array.from(wrap.querySelectorAll('.washbasin-selector__item'))
          .find(card => card.dataset.value === value);
        if (!selectedBasin || !selectedCard) return;

        const alreadySelected = selectedCard.classList.contains('is-active');

        wrap.querySelectorAll('.washbasin-selector__item').forEach(card => {
          card.classList.toggle('is-active', card === selectedCard);
        });
        pillRow.querySelectorAll('[data-design-pill]').forEach(pill => {
          pill.classList.toggle('active', pill.dataset.value === value);
        });

        wrap.classList.add('has-selection');
        pillRow.classList.add('has-selection');

        if (this.DOM.filters.design.label) {
          this.DOM.filters.design.label.textContent = selectedBasin.design_name;
        }

        if (!skipScroll) {
          this.scrollDesignCardIntoView(wrap, selectedCard, !silent);
        }

        if (!alreadySelected || silent === false) {
          onClick(selectedBasin.design_handle, selectedBasin.design_name);
        }
      };

      basins.forEach(basin => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'swatch-pill';
        pill.dataset.designPill = '';
        pill.dataset.value = basin.design_handle;
        pill.textContent = basin.design_name;
        pill.addEventListener('click', () => selectDesignValue(basin.design_handle));
        pillRow.appendChild(pill);

        const div = document.createElement('div');
        div.className = 'washbasin-selector__item swiper-slide';
        div.dataset.value = basin.design_handle;

        const productImg = basin.product_image || basin.design_image;
        const imgHtml = productImg ? `
            <div class="washbasin-selector__image-wrapper">
              <img src="${productImg}" alt="${this.escapeHtml(basin.design_name)}" loading="lazy" class="washbasin-selector__img">
            </div>
        ` : '';

        const descHtml = basin.subtitle ? `<div class="washbasin-selector__description">${this.escapeHtml(basin.subtitle)}</div>` : '';
        const matHtml = basin.material ? `<div class="washbasin-selector__material">${this.escapeHtml(basin.material)}</div>` : '';

        div.innerHTML = `
          <div class="washbasin-selector__label washbasin-selector__label--large">
            ${imgHtml}
            <div class="washbasin-selector__info">
              <div class="washbasin-selector__info-top">
                <span class="washbasin-selector__name">${this.escapeHtml(basin.design_name)}</span>
              </div>
              ${matHtml}
            </div>
            ${descHtml}
          </div>
        `;

        div.addEventListener('click', () => selectDesignValue(basin.design_handle));
        wrap.appendChild(div);
      });

      const expandable = designContainer.querySelector('.washbasin-selector__expandable');
      if (expandable) {
        expandable.classList.add('is-expanded');
      }

      const prev = designContainer.querySelector('[data-washbasin-prev]');
      const next = designContainer.querySelector('[data-washbasin-next]');

      if (this._boundDesignSliderScroll && this._designSliderEl) {
        this._designSliderEl.removeEventListener('scroll', this._boundDesignSliderScroll);
      }

      if (prev && this._boundDesignPrevClick) {
        prev.removeEventListener('click', this._boundDesignPrevClick);
      }

      if (next && this._boundDesignNextClick) {
        next.removeEventListener('click', this._boundDesignNextClick);
      }

      this._designSliderEl = wrap;
      this._boundDesignSliderScroll = () => {
        this.updateDesignSliderProgress(wrap);
        if (this._designInternalScroll) return;

        clearTimeout(this._designScrollTimer);
        this._designScrollTimer = setTimeout(() => {
          const centeredCard = this.getCenteredDesignCard(wrap);
          const nextValue = centeredCard?.dataset.value;
          if (nextValue && nextValue !== this.state.basin.design) {
            selectDesignValue(nextValue, { skipScroll: true });
          }
        }, 150);
      };

      this._boundDesignPrevClick = () => {
        const cards = Array.from(wrap.querySelectorAll('.washbasin-selector__item'));
        if (!cards.length) return;

        const currentIndex = Math.max(0, cards.findIndex(card => card.dataset.value === this.state.basin.design));
        const nextIndex = Math.max(0, currentIndex - 1);
        selectDesignValue(cards[nextIndex].dataset.value);
      };

      this._boundDesignNextClick = () => {
        const cards = Array.from(wrap.querySelectorAll('.washbasin-selector__item'));
        if (!cards.length) return;

        const currentIndex = Math.max(0, cards.findIndex(card => card.dataset.value === this.state.basin.design));
        const nextIndex = currentIndex < 0 ? 0 : Math.min(cards.length - 1, currentIndex + 1);
        selectDesignValue(cards[nextIndex].dataset.value);
      };

      wrap.addEventListener('scroll', this._boundDesignSliderScroll, { passive: true });
      if (prev) prev.addEventListener('click', this._boundDesignPrevClick);
      if (next) next.addEventListener('click', this._boundDesignNextClick);

      this.updateDesignSliderProgress(wrap);

      if (basins.length === 1) {
        selectDesignValue(basins[0].design_handle, { silent: true });
      }
    }

    // ═══════════════════════════════════════════════════════════════
    //  PRICE & ADD TO CART
    // ═══════════════════════════════════════════════════════════════

    getAddonPrice() {
        if (this.state.mode === 'basin') {
            const b = this.state.basin;
            if (!b.color || !b.thickness || !b.design || !b.native) return 0;

            const finalBasin = this.getSelectedBasinRecord();

            if (finalBasin) {
                const selectedVariant = finalBasin.variants.find(v => v.id.toString() === b.native);
                if (selectedVariant) return selectedVariant.price;
            }
        } else if (this.state.mode === 'top') {
            if (!this.state.top.variantId) return 0;
            const topProduct = this.topData.find(t => t.id == this.state.top.product);
            if (topProduct) {
                const variant = topProduct.variants.find(v => v.id.toString() === this.state.top.variantId.toString());
                if (variant) return variant.price;
            }
        }
        return 0;
    }

    getStickyAddonDetail() {
      if (this.state.mode !== 'basin' || !this.state.basin.design) {
        return {
          hasBasin: false,
          basinName: '',
          basinImage: '',
          basinVariantId: null,
          basinPrice: 0,
          mainVariantId: this.getCurrentMainVariantId ? this.getCurrentMainVariantId() : null,
          mainVariantSize: this.state.furnitureSizeRaw || '',
          bundleTotal: 0,
          bundleTotalFormatted: ''
        };
      }

      const selectedDesign = this.getAvailableBasinDesigns(this.state.basin.color, this.state.basin.thickness)
        .find((item) => item.design_handle === this.state.basin.design)
        || this.basinData.find((item) => item.design_handle === this.state.basin.design);

      const basinName = selectedDesign?.design_name || '';
      const basinImage = selectedDesign?.product_image || selectedDesign?.design_image || '';
      const addonPrice = this.getAddonPrice();
      const basePrice = this.getBasePrice();
      const bundleTotal = basePrice + addonPrice;

      return {
        hasBasin: Boolean(basinName),
        basinName,
        basinImage,
        basinVariantId: this.state.basin.native || null,
        basinPrice: addonPrice,
        mainVariantId: this.getCurrentMainVariantId ? this.getCurrentMainVariantId() : null,
        mainVariantSize: this.state.furnitureSizeRaw || '',
        bundleTotal,
        bundleTotalFormatted: bundleTotal > 0 ? this.formatMoney(bundleTotal) : ''
      };
    }

    exportSwapState() {
      const detail = this.getStickyAddonDetail();
      return {
        sectionId: this.dataset.storageScope || null,
        productId: this.productId || null,
        mode: this.state.mode,
        basin: Object.assign({}, this.state.basin),
        top: Object.assign({}, this.state.top),
        selectedBasinRecord: this.getSelectedBasinRecord(),
        stickyDetail: detail
      };
    }

    publishStickyBarState() {
      const detail = Object.assign(
        {
          sectionId: this.dataset.storageScope || null,
          productId: this.productId || null
        },
        this.getStickyAddonDetail()
      );

      window.__blAddonConfiguratorStickyState = window.__blAddonConfiguratorStickyState || {};
      const cacheKey = String(detail.sectionId || detail.productId || this.productId || 'default');
      window.__blAddonConfiguratorStickyState[cacheKey] = detail;

      document.dispatchEvent(
        new CustomEvent('bl:addon-configurator:sticky-update', {
          bubbles: true,
          detail
        })
      );
    }

    isAddonReady() {
        if (this.state.mode === 'none') return true;
        if (this.state.mode === 'basin') {
            const b = this.state.basin;
            return !!(b.color && b.thickness && b.design && b.native);
        }
        if (this.state.mode === 'top') {
            return !!this.state.top.variantId;
        }
        return false;
    }

    // Get the MINIMUM addon price based on the current filter state
    // This progressively narrows as more filters are selected
    getMinPriceForFilters() {
        const currentSize = this.state.furnitureSizeRaw;
        const b = this.state.basin;
        
        // Start with ALL basins that match the current size
        let candidates = this.basinData.filter(basin => this.basinMatchesSize(basin));
        
        // Narrow by selected filters (cascade)
        if (b.color) candidates = candidates.filter(c => c.color_handle === b.color);
        if (b.thickness) candidates = candidates.filter(c => c.thickness_handle === b.thickness);
        if (b.design) candidates = candidates.filter(c => c.design_handle === b.design);
        if (b.config) candidates = candidates.filter(c => c.config_handle === b.config);
        if (b.faucet) candidates = candidates.filter(c => c.faucet_handle === b.faucet);
        
        // Find the minimum price across all matching variants
        let minPrice = Infinity;
        candidates.forEach(basin => {
            basin.variants.forEach(v => {
                // Only count variants that match the furniture size
                if (currentSize) {
                    if (!this.variantMatchesSize(v, currentSize)) return;
                }
                if (v.price < minPrice) minPrice = v.price;
            });
        });
        
        return minPrice === Infinity ? 0 : minPrice;
    }

    updatePriceAndButton() {
        const form = this.getProductForm();
        const submitBtn = form?.querySelector('[type="submit"]');
        
        if (this.state.mode === 'none') {
            // In 'none' mode: clear price, don't disable button
            this.clearBundlePrice();
            this.publishStickyBarState();
            this.persistAddonState();
            return;
        }
        
        if (this.state.mode === 'basin') {
            const ready = this.isAddonReady();
            
            if (ready) {
                // ALL steps selected → show exact total price
                const exactPrice = this.getAddonPrice();
                if (submitBtn) submitBtn.disabled = false;
                this.showBundlePrice(exactPrice, false); // isMin = false
            } else {
                // Partial selection → show "ab X €" (cheapest possible)
                const minPrice = this.getMinPriceForFilters();
                if (submitBtn) submitBtn.disabled = true;
                if (minPrice > 0) {
                    this.showBundlePrice(minPrice, true); // isMin = true
                } else {
                    this.clearBundlePrice();
                }
            }
        } else if (this.state.mode === 'top') {
            const ready = !!this.state.top.variantId;
            if (submitBtn) submitBtn.disabled = !ready;
            if (ready) {
                const topPrice = this.getAddonPrice();
                this.showBundlePrice(topPrice, false);
            } else {
                this.clearBundlePrice();
            }
        }

        this.publishStickyBarState();
        this.persistAddonState();
    }

    getSectionId() {
        if (this._sectionId) return this._sectionId;

        const productInfo = this.closest('product-info') || document.querySelector('product-info');
        this._sectionId =
          this.dataset.storageScope
          || productInfo?.dataset.section
          || productInfo?.dataset.originalSection
          || null;

        return this._sectionId;
    }

    getCurrentMainVariantId() {
        const form = this.getProductForm();
        const variantInput = form?.querySelector('[name="id"]');
        if (variantInput?.value) return variantInput.value;

        const selectedVariantJson = this.closest('product-info')
          ?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]')
          ?.textContent;

        if (!selectedVariantJson) return null;

        try {
            const variant = JSON.parse(selectedVariantJson);
            return variant?.id ? String(variant.id) : null;
        } catch (error) {
            return null;
        }
    }

    getBasePrice() {
        const currentVariantId = this.getCurrentMainVariantId();
        if (currentVariantId && this.basePrices[currentVariantId]) {
            return parseInt(this.basePrices[currentVariantId], 10);
        }

        const selectedVariantJson = this.closest('product-info')
          ?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]')
          ?.textContent;

        if (selectedVariantJson) {
            try {
                const variant = JSON.parse(selectedVariantJson);
                if (variant?.id && this.basePrices[variant.id]) {
                    return parseInt(this.basePrices[variant.id], 10);
                }
                if (variant?.price) {
                    return parseInt(variant.price, 10);
                }
            } catch (error) {}
        }

        const mainPriceBlock = document.querySelector(`#price-${this.getSectionId()}`) || document.querySelector('[id^="price-"]');
        const priceEl = mainPriceBlock?.querySelector('.f-price-item--regular, .price-item--regular');
        if (priceEl) {
            const numStr = priceEl.textContent.replace(/[^\d.,]/g, '').replace('.', '').replace(',', '.');
            const p = parseFloat(numStr) * 100;
            if (!isNaN(p)) return p;
        }

        return 0;
    }

    getBaseCompareAtPrice() {
        const currentVariantId = this.getCurrentMainVariantId();
        if (currentVariantId && this.baseCompareAtPrices[currentVariantId]) {
            return parseInt(this.baseCompareAtPrices[currentVariantId], 10);
        }

        const selectedVariantJson = this.closest('product-info')
          ?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]')
          ?.textContent;

        if (selectedVariantJson) {
            try {
                const variant = JSON.parse(selectedVariantJson);
                if (variant?.id && this.baseCompareAtPrices[variant.id]) {
                    return parseInt(this.baseCompareAtPrices[variant.id], 10);
                }
                if (variant?.compare_at_price) {
                    return parseInt(variant.compare_at_price, 10);
                }
            } catch (error) {}
        }

        return 0;
    }

    formatMoney(cents) {
        // Round to nearest whole euro (no decimals)
        cents = Math.round(cents);
        if (window.Shopify && window.Shopify.formatMoney) {
            // Force whole numbers by using a format without decimals
            return window.Shopify.formatMoney(cents, '{{amount_no_decimals}} €');
        }
        // Fallback for EUR — always whole numbers
        return Math.round(cents / 100).toLocaleString('de-DE') + ' €';
    }

    ensurePriceOverrideCSS() {
        window.BLBundlePriceState?.ensureCSS?.();
    }

    buildBundlePriceState(addonPrice, isMin = false) {
        const basePrice = this.getBasePrice();
        const total = basePrice + addonPrice;
        const formattedTotal = this.formatMoney(total);
        const baseCompareAtPrice = this.getBaseCompareAtPrice();
        const hasDiscount = baseCompareAtPrice > basePrice && basePrice > 0 && addonPrice > 0 && !isMin;
        let compareAtTotal = 0;

        if (hasDiscount) {
            compareAtTotal = Math.round((total * baseCompareAtPrice) / basePrice);
        }

        return {
            total,
            formattedTotal,
            displayPrice: `${isMin ? 'ab ' : ''}${formattedTotal}`,
            compareAtTotal,
            compareAtPrice: compareAtTotal > total ? this.formatMoney(compareAtTotal) : '',
            isOnSale: compareAtTotal > total
        };
    }

    getPriceTargets() {
        const targets = [];
        const mainPrice = document.querySelector(`#price-${this.getSectionId()}`) || document.querySelector('product-info [id^="price-"]');
        if (mainPrice) targets.push(mainPrice);

        document.querySelectorAll('#shopify-section-sticky-atc-bar sticky-atc-bar .sticky-atc-bar__product-info').forEach((target) => {
            if (target.querySelector('.f-price')) {
                targets.push(target);
            }
        });

        return targets;
    }

    syncBundlePriceClasses(isActive) {
        window.BLBundlePriceState?.setOwnerState?.('addon-configurator', isActive, { buttonReady: isActive });
    }

    getVariantSizeValue(variant, fallback = this.state.furnitureSizeRaw) {
        if (!variant?.options?.length) return fallback || null;
        return variant.options.find((option) => this.looksLikeSizeValue(option)) || fallback || null;
    }

    getBasinPreviewCandidates(targetSize, basinState = this.state.basin) {
        const selectionLevels = [
            ['color', 'thickness', 'design', 'config', 'faucet'],
            ['color', 'thickness', 'design', 'config'],
            ['color', 'thickness', 'design'],
            ['color', 'thickness'],
            ['color']
        ];

        const getCandidatesForKeys = (keys, stateForKeys = basinState) => {
            return this.basinData.filter((basin) => {
                if (!Array.isArray(basin.variants) || !basin.variants.length) return false;
                if (!basin.variants.some((variant) => this.variantMatchesSize(variant, targetSize))) return false;

                return keys.every((key) => {
                    const selectedValue = stateForKeys?.[key];
                    if (!selectedValue && selectedValue !== '') return true;

                    const recordKey = `${key}_handle`;
                    return basin[recordKey] === selectedValue;
                });
            });
        };

        for (const keys of selectionLevels) {
            const hasAnySelection = keys.some((key) => Boolean(basinState?.[key] || basinState?.[key] === ''));
            if (!hasAnySelection) continue;

            const candidates = getCandidatesForKeys(keys);
            if (candidates.length) return candidates;
        }

        const fallbackColors = [];
        const seenColors = new Set();
        this.basinData.forEach((basin) => {
            if (!Array.isArray(basin.variants) || !basin.variants.length) return;
            if (!basin.variants.some((variant) => this.variantMatchesSize(variant, targetSize))) return;
            if (!basin.color_handle || seenColors.has(basin.color_handle)) return;

            seenColors.add(basin.color_handle);
            fallbackColors.push(basin.color_handle);
        });

        for (const colorHandle of fallbackColors) {
            const candidates = getCandidatesForKeys(['color'], { color: colorHandle });
            if (candidates.length) return candidates;
        }

        return [];
    }

    getTopPreviewProduct(targetSize) {
        const currentTopId = this.state.top?.product;
        const currentTop = this.topData.find((item) => String(item.id) === String(currentTopId));
        if (currentTop?.variants?.some((variant) => this.variantMatchesSize(variant, targetSize))) {
            return currentTop;
        }

        return this.topData.find((item) =>
            Array.isArray(item.variants) && item.variants.some((variant) => this.variantMatchesSize(variant, targetSize))
        ) || currentTop || this.topData[0] || null;
    }

    getBundlePreviewForVariant(variant) {
        const basePrice = Number.parseInt(variant?.price || 0, 10);
        if (!basePrice || this.state.mode === 'none') return null;

        const targetSize = this.getVariantSizeValue(variant);

        if (this.state.mode === 'basin') {
            const candidates = this.getBasinPreviewCandidates(targetSize);
            if (!candidates.length) return null;

            let matchedVariant = null;
            if (this.state.basin?.native) {
                candidates.some((basin) => {
                    matchedVariant = basin.variants.find((candidateVariant) =>
                        String(candidateVariant.id) === String(this.state.basin.native)
                        && this.variantMatchesSize(candidateVariant, targetSize)
                    ) || null;

                    return Boolean(matchedVariant);
                });
            }

            let addonPrice = matchedVariant ? Number.parseInt(matchedVariant.price || 0, 10) : 0;
            let isMin = false;

            if (!addonPrice) {
                addonPrice = candidates.reduce((minPrice, basin) => {
                    basin.variants.forEach((candidateVariant) => {
                        if (!this.variantMatchesSize(candidateVariant, targetSize)) return;

                        const candidatePrice = Number.parseInt(candidateVariant.price || 0, 10);
                        if (candidatePrice > 0 && candidatePrice < minPrice) {
                            minPrice = candidatePrice;
                        }
                    });

                    return minPrice;
                }, Number.POSITIVE_INFINITY);

                if (!Number.isFinite(addonPrice)) return null;
                isMin = true;
            }

            const bundlePriceState = this.buildBundlePriceState(addonPrice, isMin);

            return {
                total: bundlePriceState.total,
                formattedTotal: bundlePriceState.formattedTotal,
                displayPrice: bundlePriceState.displayPrice,
                compareAtTotal: bundlePriceState.compareAtTotal,
                compareAtPrice: bundlePriceState.compareAtPrice,
                isOnSale: bundlePriceState.isOnSale,
                isMin
            };
        }

        if (this.state.mode === 'top') {
            const topProduct = this.getTopPreviewProduct(targetSize);
            if (!topProduct?.variants?.length) return null;

            const matchedVariant = topProduct.variants.find((candidateVariant) =>
                this.variantMatchesSize(candidateVariant, targetSize)
            ) || topProduct.variants.find((candidateVariant) =>
                String(candidateVariant.id) === String(this.state.top?.variantId)
            ) || topProduct.variants[0];

            const addonPrice = Number.parseInt(matchedVariant?.price || 0, 10);
            if (!addonPrice) return null;

            const bundlePriceState = this.buildBundlePriceState(addonPrice, false);

            return {
                total: bundlePriceState.total,
                formattedTotal: bundlePriceState.formattedTotal,
                displayPrice: bundlePriceState.displayPrice,
                compareAtTotal: bundlePriceState.compareAtTotal,
                compareAtPrice: bundlePriceState.compareAtPrice,
                isOnSale: bundlePriceState.isOnSale,
                isMin: false
            };
        }

        return null;
    }

    injectBundlePriceIntoNode(priceNode, priceState) {
        if (!priceNode || !priceState) return;
        this.ensurePriceOverrideCSS();
        window.BLBundlePriceState?.applyPriceNode?.(priceNode, priceState, { lock: true });
        this.syncBundlePriceClasses(true);
    }

    showBundlePrice(addonPrice, isMin) {
        const bundlePriceState = this.buildBundlePriceState(addonPrice, isMin);
        const displayPrice = bundlePriceState.displayPrice;

        this.ensurePriceOverrideCSS();
        this.syncBundlePriceClasses(true);

        this.getPriceTargets().forEach((target) => {
            window.BLBundlePriceState?.applyPriceNode?.(target, bundlePriceState, { lock: false });
        });

        document.querySelectorAll('[data-persistent-btn-price]').forEach(wrapper => {
            wrapper.dataset.btnPriceSource = 'bundle';
            const priceDisplay = wrapper.querySelector('[data-btn-price-display]');
            if (priceDisplay) {
                priceDisplay.textContent = displayPrice;
            }
        });

        this._lastRenderedBundlePrice = displayPrice;
        window.BLAtcPriceSync?.applyBundlePrice('addon-configurator', displayPrice);
    }

    clearBundlePrice() {
        window.BLAtcPriceSync?.clearBundlePrice('addon-configurator');
        this.syncBundlePriceClasses(false);

        document.querySelectorAll('[data-btn-price-wrapper]').forEach(el => {
            if (el.hasAttribute('data-persistent-btn-price')) {
                el.dataset.btnPriceSource = 'default';
                const priceDisplay = el.querySelector('[data-btn-price-display]');
                const defaultPrice = el.dataset.defaultPrice || '';
                if (priceDisplay && defaultPrice) {
                    priceDisplay.textContent = defaultPrice;
                }
                return;
            }
        });

        this.getPriceTargets().forEach((target) => {
            window.BLBundlePriceState?.clearPriceNode?.(target);
        });

        this._lastRenderedBundlePrice = null;
    }

    getActiveAddonVariantId() {
      if (this.state.mode === 'basin' && this.state.basin.native && this.isAddonReady()) {
        return this.state.basin.native;
      }

      if (this.state.mode === 'top' && this.state.top.variantId) {
        return this.state.top.variantId;
      }

      return null;
    }

    buildBundlePayload(config) {
      if (!this.isConnected || this.state.mode === 'none') return null;

      const addonVariantId = this.getActiveAddonVariantId();
      if (!addonVariantId) return null;

      let items = [];
      let sections = '';
      let bundleId = null;

      if (config.body instanceof FormData) {
          const formData = config.body;
          const mainId = formData.get('id');
          const mainQty = formData.get('quantity') || 1;
          sections = formData.get('sections') || formData.get('sections_url') || '';
          bundleId = buildStableBundleId(mainId, addonVariantId);

          const mainProps = { '_msw_bundle_id': bundleId, '_linkedTo': addonVariantId };
          for (let [key, value] of formData.entries()) {
              if (key.startsWith('properties[')) {
                  const propName = key.match(/properties\[(.*?)\]/)[1];
                  mainProps[propName] = value;
              }
          }

          items = [
              { id: parseInt(mainId, 10), quantity: parseInt(mainQty, 10), properties: mainProps },
              { id: parseInt(addonVariantId, 10), quantity: 1, properties: { '_msw_bundle_id': bundleId, '_isAddon': 'true', '_linkedTo': mainId } }
          ];
      } else if (typeof config.body === 'string') {
          try {
              const body = JSON.parse(config.body);
              const mainId = body.id || (body.items && body.items[0]?.id);
              const mainQty = body.quantity || (body.items && body.items[0]?.quantity) || 1;
              sections = body.sections || body.sections_url || '';
              bundleId = buildStableBundleId(mainId, addonVariantId);

              const mainProps = Object.assign({}, (body.properties || (body.items && body.items[0]?.properties) || {}));
              mainProps['_msw_bundle_id'] = bundleId;
              mainProps['_linkedTo'] = addonVariantId;

              items = [
                  { id: parseInt(mainId, 10), quantity: parseInt(mainQty, 10), properties: mainProps },
                  { id: parseInt(addonVariantId, 10), quantity: 1, properties: { '_msw_bundle_id': bundleId, '_isAddon': 'true', '_linkedTo': mainId } }
              ];
          } catch(e) {}
      }

      if (!items.length) return null;

      return { items, sections };
    }

    initAddToCart() {
      const registry = installCartAddonFetchInterceptor();

      registry.providers.set(this._fetchProviderId, {
        priority: 30,
        getBundlePayload: (config) => this.buildBundlePayload(config)
      });
    }

    getProductForm() {
      const root = this.closest('product-info') ||
                   document.querySelector(`product-info[data-section="${this.closest('[data-section]')?.dataset.section || ''}"]`) ||
                   document.querySelector('product-info[id^="MainProduct-"]') ||
                   document.querySelector('product-info');

      return root?.querySelector('form.main-product-form[action*="/cart/add"]') ||
             root?.querySelector('form.main-product-form') ||
             root?.querySelector('form[action*="/cart/add"]:not(.installment)') ||
             root?.querySelector('form[action*="/cart/add"]') ||
             document.querySelector('form[action*="/cart/add"]');
    }

    initDynamicCheckoutBridge() {
      this._boundDynamicCheckoutClick = (event) => {
        const button = event.target.closest('.shopify-payment-button__button--unbranded');
        if (!button) return;

        const form = this.getProductForm();
        if (!form || !button.closest('form') || button.closest('form') !== form) return;
        if (this._dynamicCheckoutPending) return;

        const payload = this.buildBundlePayload({ body: new FormData(form) });
        if (!payload || !Array.isArray(payload.items) || payload.items.length < 2) return;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        this._dynamicCheckoutPending = true;
        button.setAttribute('disabled', 'disabled');
        button.classList.add('is-loading');

        const registry = getCartAddonInterceptorRegistry();
        registry.originalFetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: payload.items })
        })
          .then(async (response) => {
            if (!response.ok) {
              let message = 'Dynamic checkout bundle add failed';
              try {
                const error = await response.json();
                message = error?.description || error?.message || message;
              } catch (error) {}
              throw new Error(message);
            }

            window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
          })
          .catch((error) => {
            console.error('[BL-Customizer] Dynamic checkout bridge failed', error);
            button.removeAttribute('disabled');
            button.classList.remove('is-loading');
            this._dynamicCheckoutPending = false;
          });
      };

      document.addEventListener('click', this._boundDynamicCheckoutClick, true);
    }

    destroy() {
      clearTimeout(this._sizeSyncTimer);
      clearTimeout(this._designScrollTimer);
      clearTimeout(this._designInternalScrollTimer);

      if (this._variantPicker && this._boundPickerSizeSync) {
        this._variantPicker.removeEventListener('click', this._boundPickerSizeSync);
        this._variantPicker.removeEventListener('change', this._boundPickerSizeSync);
      }

      if (this._designSliderEl && this._boundDesignSliderScroll) {
        this._designSliderEl.removeEventListener('scroll', this._boundDesignSliderScroll);
      }

      const designContainer = this.DOM.filters.design?.container;
      const prev = designContainer?.querySelector('[data-washbasin-prev]');
      const next = designContainer?.querySelector('[data-washbasin-next]');

      if (prev && this._boundDesignPrevClick) {
        prev.removeEventListener('click', this._boundDesignPrevClick);
      }

      if (next && this._boundDesignNextClick) {
        next.removeEventListener('click', this._boundDesignNextClick);
      }

      if (this._boundVariantChanged) {
        document.removeEventListener('variant:changed', this._boundVariantChanged);
      }

      if (this._boundDynamicCheckoutClick) {
        document.removeEventListener('click', this._boundDynamicCheckoutClick, true);
      }

      const registry = getCartAddonInterceptorRegistry();
      registry.providers.delete(this._fetchProviderId);
    }
  }

  customElements.define('bl-product-customizer', BLProductCustomizer);
}
